import { PropsWithChildren } from 'react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, ButtonBar, Heading, Text } from '@frontend/design-system';
import { ReadOnlyReviewAppointmentRequestForm } from '../../../../../../../components/ReadOnlyReviewAppointmentRequestForm';
import { ScheduleReadOnlyRequestDetailsContextProvider, useScheduleReadOnlyRequestDetailsContext } from '../../Context';
import { useScheduleReadOnlyRequestDetails } from '../../hooks';
import { ScheduleRequestsTrackingIds } from '../../trackingIds';
import { ScheduleRequestModalManager } from '../ScheduleRequestModalManager';

const ScheduleReadOnlyRequestHeader = () => {
  const { t } = useTranslation('scheduleCalendarRequest');
  return (
    <header>
      <Heading level={2}>{t('Review Appointment Request')}</Heading>
      <Text css={{ marginTop: theme.spacing(1) }} size='medium' color='light'>
        {t('Review and confirm the appointment request details below.')}
      </Text>
    </header>
  );
};

const ScheduleReadOnlyRequestBody = () => {
  const {
    selectedScheduleRequest,
    locationName,
    requestedProviderName,
    requestedDateTimes,
    requestedDateTimeProps,
    hasCustomDateTimeFields,
    customDateField,
    customStartTimeField,
    customEndTimeField,
    isValidCustomEndTime,
    setHasCustomDateTimeFields,
    scheduleRequestConfirmedTemplate,
    tags,
    isComplete,
    isPaymentsEnabled,
    paidAmount,
    setMessageTemplate,
  } = useScheduleReadOnlyRequestDetailsContext();

  return (
    <section css={{ height: '100%', backgroundColor: theme.colors.neutral5, minHeight: 400, overflowY: 'auto' }}>
      <ReadOnlyReviewAppointmentRequestForm
        requestDetails={selectedScheduleRequest}
        locationName={locationName}
        providerName={requestedProviderName}
        requestedDateTimes={requestedDateTimes as string[]}
        requestedDateTimeProps={requestedDateTimeProps}
        hasCustomDateTimeFields={hasCustomDateTimeFields}
        customDateField={customDateField}
        customStartTimeField={customStartTimeField}
        customEndTimeField={customEndTimeField}
        isValidCustomEndTime={isValidCustomEndTime}
        setHasCustomDateTimeFields={setHasCustomDateTimeFields}
        template={scheduleRequestConfirmedTemplate?.Template ?? ''}
        templateTags={tags}
        isReadOnlyTemplate={!isComplete}
        onTemplateChange={(template) => setMessageTemplate(template)}
        isPaymentsEnabled={isPaymentsEnabled}
        paidAmount={paidAmount}
      />
    </section>
  );
};

const ScheduleReadOnlyRequestFooter = () => {
  const {
    modalType,
    setModalType,
    handleCloseScheduleRequestModal,
    approveRequest,
    disableApproveButton,
    messageOnClick,
    locationId,
    phoneNumber,
    personId,
    closeActionsModal,
    fullName,
    handleCloseModal,
  } = useScheduleReadOnlyRequestDetailsContext();
  const { t } = useTranslation('scheduleCalendarRequest');

  return (
    <>
      <footer
        css={{
          padding: theme.spacing(2),
          backgroundColor: theme.colors.white,
          position: 'static',
          bottom: 0,
          boxShadow: theme.shadows.heavy,
        }}
      >
        <ButtonBar>
          <Button
            variant='tertiary'
            label={t('Message Patient')}
            trackingId={ScheduleRequestsTrackingIds.requestFormMessageLinkBtn}
            onClick={async () => {
              await messageOnClick({ threadGroupId: locationId, personPhone: phoneNumber, personId });
              handleCloseScheduleRequestModal();
              closeActionsModal();
            }}
            iconName='message-small'
            color='primary'
          >
            {t('Message')}
          </Button>
          <Button
            variant='secondary'
            label={t('Close Schedule Request Modal')}
            onClick={handleCloseScheduleRequestModal}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant='primary'
            label={t('Approve Request')}
            iconName='check-small'
            disabled={disableApproveButton}
            trackingId={ScheduleRequestsTrackingIds.requestApproveBtn}
            onClick={() => {
              approveRequest();
            }}
          >
            {t('Approve')}
          </Button>
        </ButtonBar>
      </footer>
      <ScheduleRequestModalManager
        modalType={modalType}
        onConfirm={() => {}}
        fullName={fullName}
        onClose={handleCloseModal}
        setModalType={setModalType}
      />
    </>
  );
};

type ScheduleReadOnlyRequestModalProps = ReturnType<typeof useScheduleReadOnlyRequestDetails>;

const ScheduleReadOnlyRequestDetailsProvider = ({
  children,
  ...props
}: PropsWithChildren<ScheduleReadOnlyRequestModalProps>) => {
  return (
    <ScheduleReadOnlyRequestDetailsContextProvider value={props}>
      {children}
    </ScheduleReadOnlyRequestDetailsContextProvider>
  );
};

export const ScheduleReadOnlyRequestDetails = Object.assign(ScheduleReadOnlyRequestDetailsProvider, {
  Header: ScheduleReadOnlyRequestHeader,
  Body: ScheduleReadOnlyRequestBody,
  Footer: ScheduleReadOnlyRequestFooter,
});
